import React, { FC } from "react"
import { KSpan, KTitleSpan } from "kahuna-base-react-components"
import { AdvanceDealRow } from "../../../../../types"

const TEXT_MUTED = "#6b7280"
const TEXT_PRIMARY = "#111"
const GROUP_BG = "#f3f4f6"
const GROUP_BORDER = "#e5e7eb"
const NA = "-"

// "YYYYMM" or an ISO timestamp -> "Jun 2025"
const monthYear = (value: string | null | undefined) => {
  if (!value) return NA
  const key = String(value)
  const compact = /^\d{6}$/.test(key)
  const date = compact ? new Date(parseInt(key.slice(0, 4), 10), parseInt(key.slice(4, 6), 10) - 1, 1) : new Date(key)
  return isNaN(date.getTime()) ? NA : date.toLocaleDateString("en-US", { month: "short", year: "numeric" })
}

const percent = (value: number | string | null | undefined) =>
  value === null || value === undefined || value === "" || isNaN(Number(value)) ? NA : `${Number(value)}%`

const yesNo = (value: boolean | null | undefined) => (value === null || value === undefined ? NA : value ? "Yes" : "No")

// The contract length in years the card shows, or null when it is not known
const lengthInYears = (deal: AdvanceDealRow) => {
  const value = Number(deal.contractModel === "post_recoup" ? deal.postRecoupDuration : deal.contractDuration)
  return isNaN(value) || value <= 0 ? null : value
}

const contractLength = (deal: AdvanceDealRow) => {
  const length = lengthInYears(deal)
  if (length === null) return NA
  const text = `${length} ${length === 1 ? "year" : "years"}`
  return deal.contractModel === "post_recoup" ? `${text} post-recoup` : text
}

// The backend's contract end when it has one; otherwise the contract start plus the contract length
const contractEnd = (deal: AdvanceDealRow, start: string | null | undefined) => {
  if (deal.contractEnd) return monthYear(deal.contractEnd)
  const length = lengthInYears(deal)
  if (!start || length === null) return NA
  const compact = /^\d{6}$/.test(String(start))
  const date = compact
    ? new Date(parseInt(start.slice(0, 4), 10), parseInt(start.slice(4, 6), 10) - 1, 1)
    : new Date(start)
  if (isNaN(date.getTime())) return NA
  date.setFullYear(date.getFullYear() + length)
  return `${monthYear(`${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, "0")}`)} (est.)`
}

const contractModelText = (model: string | null | undefined) =>
  model === "post_recoup" ? "Post-Recoup" : model ? "Fixed Term" : NA

const Group: FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="flex flex-col gap-2">
    <KSpan text={title.toUpperCase()} fontSize={11} fontWeight={700} color={TEXT_MUTED} letterSpacing="0.03em" />
    <div style={{ background: GROUP_BG, border: `1px solid ${GROUP_BORDER}`, borderRadius: 10, padding: "12px 14px" }}>
      {children}
    </div>
  </div>
)

const Stat: FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex flex-col gap-1">
    <KSpan
      text={label.toUpperCase()}
      fontSize={10.5}
      fontWeight={600}
      color={TEXT_MUTED}
      letterSpacing="0.03em"
      lineHeight="14px"
    />
    <KSpan text={value} fontSize={13} fontWeight={600} color={TEXT_PRIMARY} lineHeight="18px" />
  </div>
)

const Row: FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex justify-between items-baseline gap-3 py-1">
    <KSpan text={label} fontSize={12.5} color={TEXT_MUTED} lineHeight="18px" />
    <KSpan text={value} fontSize={12.5} fontWeight={600} color={TEXT_PRIMARY} lineHeight="18px" />
  </div>
)

const cellStyle = (first: boolean, top: boolean): React.CSSProperties => ({
  padding: top ? "0 0 8px" : "7px 0",
  textAlign: first ? "left" : "right",
  borderTop: top ? undefined : `1px solid ${GROUP_BORDER}`
})

const ContractTermsCard: FC<{ selectedDeal: AdvanceDealRow }> = ({ selectedDeal }) => {
  const { dealOffer, userExpense } = selectedDeal
  const contractStart = dealOffer?.signedAt || selectedDeal.dealDate
  // New releases pay the distribution fee; the rest of the catalogue (takedowns) the commission.
  const commissions = [
    { label: "New Release", pre: userExpense?.distributionFee, post: userExpense?.distributionFeePostRecoup },
    { label: "Takedown", pre: userExpense?.preRecoupCommission, post: userExpense?.postRecoupCommission }
  ]

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 flex flex-col gap-4 min-w-0">
      <KTitleSpan text="Contract Terms" fontSize={20} fontWeight={700} lineHeight="28px" />

      <Group title="Contract Overview">
        <div className="grid grid-cols-2 gap-x-4 gap-y-3">
          <Stat label="Signed Date" value={monthYear(contractStart)} />
          <Stat label="Contract End Date" value={contractEnd(selectedDeal, contractStart)} />
          <Stat label="Contract Model" value={contractModelText(selectedDeal.contractModel)} />
          <Stat label="Contract Length" value={contractLength(selectedDeal)} />
        </div>
      </Group>

      <Group title="Scope & Rights">
        <Row label="New Releases Included In Recoupment" value={yesNo(dealOffer?.newReleaseIncludeRecoup)} />
        <Row label="Exclusivity" value={yesNo(dealOffer?.contractExclusivity ?? userExpense?.exclusivity)} />
        <Row label="Admin Fee" value={percent(userExpense?.adminFee)} />
      </Group>

      <Group title="Commission Structure">
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              {["", "Pre-Recoup", "Post-Recoup"].map((heading, index) => (
                <th key={index} style={cellStyle(index === 0, true)}>
                  <KSpan
                    text={heading.toUpperCase()}
                    fontSize={10.5}
                    fontWeight={600}
                    color={TEXT_MUTED}
                    letterSpacing="0.03em"
                    lineHeight="14px"
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {commissions.map((row) => (
              <tr key={row.label}>
                <td style={cellStyle(true, false)}>
                  <KSpan text={row.label} fontSize={12.5} color={TEXT_MUTED} lineHeight="18px" />
                </td>
                <td style={cellStyle(false, false)}>
                  <KSpan
                    text={percent(row.pre)}
                    fontSize={12.5}
                    fontWeight={600}
                    color={TEXT_PRIMARY}
                    lineHeight="18px"
                  />
                </td>
                <td style={cellStyle(false, false)}>
                  <KSpan
                    text={percent(row.post)}
                    fontSize={12.5}
                    fontWeight={600}
                    color={TEXT_PRIMARY}
                    lineHeight="18px"
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Group>

      <Group title="Advance Context">
        <Row label="First Advance Deployment Date" value={monthYear(selectedDeal.firstAdvanceDate)} />
        <Row label="Latest Advance Deployment Date" value={monthYear(selectedDeal.latestAdvanceDate)} />
      </Group>
    </div>
  )
}

export default ContractTermsCard
